import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { SwalAlert } from '@/hooks/useSweetAlert';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { exploreRadios, planLabels, planColors, statusLabels, statusColors, categoryLabels } from '@/mocks/adminExploreRadios';
import type { ExploreRadio, RadioPlan, RadioStatus, RadioCategory } from '@/mocks/adminExploreRadios';

const allPlans: Array<RadioPlan | 'todos'> = ['todos', 'free', 'pro', 'premium'];
const allStatuses: Array<RadioStatus | 'todos'> = ['todos', 'activa', 'inactiva', 'suspendida'];
const allCategories: Array<RadioCategory | 'todos'> = ['todos', 'musical', 'noticias', 'deportiva', 'cultural', 'religiosa', 'comunitaria', 'juvenil', 'variada'];

function formatNumber(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

export default function AdminExploreRadios() {
  const { isPremium, planId } = usePlanPermissions();

  const [radios, setRadios] = useState<ExploreRadio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState<RadioPlan | 'todos'>('todos');
  const [filterStatus, setFilterStatus] = useState<RadioStatus | 'todos'>('todos');
  const [filterCategory, setFilterCategory] = useState<RadioCategory | 'todos'>('todos');
  const [sortBy, setSortBy] = useState<'listeners' | 'rating' | 'name' | 'createdAt'>('listeners');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedRadio, setExpandedRadio] = useState<number | null>(null);

  const loadRadios = useCallback(() => {
    setLoading(true);
    setError(null);
    setTimeout(() => {
      try {
        setRadios([...exploreRadios]);
        setLoading(false);
      } catch {
        setError('No se pudieron cargar las radios. Verificá tu conexión e intentá de nuevo.');
        setLoading(false);
      }
    }, 650);
  }, []);

  useEffect(() => {
    loadRadios();
  }, [loadRadios]);

  const filteredRadios = useMemo(() => {
    let result = radios.filter((r) => {
      const matchSearch =
        !search ||
        r.name.toLowerCase().includes(search.toLowerCase()) ||
        r.frequency.toLowerCase().includes(search.toLowerCase()) ||
        r.location.toLowerCase().includes(search.toLowerCase()) ||
        r.owner.toLowerCase().includes(search.toLowerCase()) ||
        r.slogan.toLowerCase().includes(search.toLowerCase());
      const matchPlan = filterPlan === 'todos' || r.plan === filterPlan;
      const matchStatus = filterStatus === 'todos' || r.status === filterStatus;
      const matchCategory = filterCategory === 'todos' || r.category === filterCategory;
      return matchSearch && matchPlan && matchStatus && matchCategory;
    });

    result.sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'listeners') cmp = a.listeners - b.listeners;
      else if (sortBy === 'rating') cmp = a.rating - b.rating;
      else if (sortBy === 'name') cmp = a.name.localeCompare(b.name);
      else if (sortBy === 'createdAt') cmp = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [radios, search, filterPlan, filterStatus, filterCategory, sortBy, sortDir]);

  const stats = useMemo(() => {
    const total = radios.length;
    const activas = radios.filter((r) => r.status === 'activa').length;
    const inactivas = radios.filter((r) => r.status === 'inactiva').length;
    const suspendidas = radios.filter((r) => r.status === 'suspendida').length;
    const totalListeners = radios.reduce((sum, r) => sum + r.listeners, 0);
    const avgRating = radios.length > 0 ? radios.reduce((sum, r) => sum + r.rating, 0) / radios.length : 0;
    const premiumCount = radios.filter((r) => r.plan === 'premium').length;
    const proCount = radios.filter((r) => r.plan === 'pro').length;
    const freeCount = radios.filter((r) => r.plan === 'free').length;
    return { total, activas, inactivas, suspendidas, totalListeners, avgRating, premiumCount, proCount, freeCount };
  }, [radios]);

  const planCounts = useMemo(() => {
    const counts: Record<string, number> = { todos: radios.length };
    allPlans.forEach((p) => {
      if (p !== 'todos') counts[p] = radios.filter((r) => r.plan === p).length;
    });
    return counts;
  }, [radios]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { todos: radios.length };
    allStatuses.forEach((s) => {
      if (s !== 'todos') counts[s] = radios.filter((r) => r.status === s).length;
    });
    return counts;
  }, [radios]);

  const handleToggleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const handleViewRadio = async (radio: ExploreRadio) => {
    const escHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
    const { value: action } = await Swal.fire({
      title: radio.name,
      html: `
        <div class="text-left space-y-3">
          <div class="flex items-center gap-3">
            <div class="w-16 h-12 rounded-lg overflow-hidden bg-background-200 shrink-0">
              <img src="${radio.imageUrl}" alt="${escHtml(radio.name)}" class="w-full h-full object-cover" />
            </div>
            <div>
              <p class="text-sm font-bold text-foreground-900">${escHtml(radio.name)}</p>
              <p class="text-xs text-foreground-500">${escHtml(radio.frequency)} · ${escHtml(radio.location)}, ${escHtml(radio.country)}</p>
            </div>
          </div>
          <div class="bg-background-100 rounded-xl p-3 space-y-1.5 text-xs">
            <div class="flex justify-between"><span class="text-foreground-500">Plan</span><span class="font-bold">${planLabels[radio.plan]}</span></div>
            <div class="flex justify-between"><span class="text-foreground-500">Estado</span><span class="font-bold">${statusLabels[radio.status]}</span></div>
            <div class="flex justify-between"><span class="text-foreground-500">Oyentes</span><span class="font-bold">${formatNumber(radio.listeners)}</span></div>
            <div class="flex justify-between"><span class="text-foreground-500">Rating</span><span class="font-bold">${radio.rating.toFixed(1)} ★</span></div>
            <div class="flex justify-between"><span class="text-foreground-500">Dueño</span><span class="font-bold">${escHtml(radio.owner)}</span></div>
          </div>
          <p class="text-xs text-foreground-400 italic">"${escHtml(radio.slogan)}"</p>
        </div>
      `,
      icon: 'info',
      showCancelButton: true,
      confirmButtonText: '<i class="ri-external-link-line mr-1"></i> Visitar radio',
      cancelButtonText: 'Cerrar',
      customClass: {
        popup: 'rounded-2xl max-w-sm',
        title: 'font-bold text-sm',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-primary-500 text-background-50',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap bg-background-100 text-foreground-600',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    });
    if (action.isConfirmed) {
      window.open(`https://radiofrecuenciaweb.com/r/${radio.slug || radio.id}`, '_blank', 'noopener,noreferrer');
    }
  };

  const hasFilters = filterPlan !== 'todos' || filterStatus !== 'todos' || filterCategory !== 'todos' || search;

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-compass-3-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Explorar Radios</h1>
              <p className="text-foreground-400 text-[9px]">{stats.total} radios &bull; {stats.activas} activas</p>
            </div>
          </div>
          <Link
            to="/admin/dashboard"
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div>
              <h2 className="text-foreground-950 font-bold text-base">Explorar Radios</h2>
              <p className="text-foreground-400 text-xs">
                {stats.total} radios en la plataforma &bull; {stats.activas} activas &bull; {formatNumber(stats.totalListeners)} oyentes totales
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/admin/dashboard"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-dashboard-line text-xs"></i>
                Dashboard
              </Link>
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">
            {/* Stats top cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <button
                onClick={() => { setFilterPlan('todos'); setFilterStatus('todos'); }}
                className="flex flex-col items-start gap-1 p-3 rounded-2xl border bg-background-50 border-background-200 hover:border-foreground-300 cursor-pointer transition-all"
              >
                <i className="ri-radio-line text-foreground-400 text-lg"></i>
                <span className="text-xl font-bold text-foreground-900">{stats.total}</span>
                <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Total</span>
              </button>
              <button
                onClick={() => setFilterStatus(filterStatus === 'activa' ? 'todos' : 'activa')}
                className={`flex flex-col items-start gap-1 p-3 rounded-2xl border cursor-pointer transition-all ${
                  filterStatus === 'activa'
                    ? 'bg-emerald-50 border-emerald-300'
                    : 'bg-background-50 border-background-200 hover:border-emerald-300'
                }`}
              >
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                <span className="text-xl font-bold text-foreground-900">{stats.activas}</span>
                <span className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wide">Activas</span>
              </button>
              <button
                onClick={() => setFilterPlan(filterPlan === 'premium' ? 'todos' : 'premium')}
                className={`flex flex-col items-start gap-1 p-3 rounded-2xl border cursor-pointer transition-all ${
                  filterPlan === 'premium'
                    ? 'bg-accent-50 border-accent-300'
                    : 'bg-background-50 border-background-200 hover:border-accent-300'
                }`}
              >
                <i className="ri-vip-diamond-fill text-accent-500 text-lg"></i>
                <span className="text-xl font-bold text-foreground-900">{stats.premiumCount}</span>
                <span className="text-[10px] font-semibold text-accent-600 uppercase tracking-wide">Premium</span>
              </button>
              <button
                onClick={() => setFilterPlan(filterPlan === 'pro' ? 'todos' : 'pro')}
                className={`flex flex-col items-start gap-1 p-3 rounded-2xl border cursor-pointer transition-all ${
                  filterPlan === 'pro'
                    ? 'bg-primary-50 border-primary-300'
                    : 'bg-background-50 border-background-200 hover:border-primary-300'
                }`}
              >
                <i className="ri-medal-line text-primary-500 text-lg"></i>
                <span className="text-xl font-bold text-foreground-900">{stats.proCount}</span>
                <span className="text-[10px] font-semibold text-primary-600 uppercase tracking-wide">Pro</span>
              </button>
              <div className="flex flex-col items-start gap-1 p-3 rounded-2xl border bg-background-50 border-background-200">
                <i className="ri-user-voice-line text-foreground-400 text-lg"></i>
                <span className="text-xl font-bold text-foreground-900">{formatNumber(stats.totalListeners)}</span>
                <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Oyentes</span>
              </div>
              <div className="flex flex-col items-start gap-1 p-3 rounded-2xl border bg-background-50 border-background-200">
                <i className="ri-star-fill text-amber-400 text-lg"></i>
                <span className="text-xl font-bold text-foreground-900">{stats.avgRating.toFixed(1)}</span>
                <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Rating prom.</span>
              </div>
            </div>

            {/* Filters bar */}
            <div className="bg-background-50 rounded-2xl border border-background-200 p-4 space-y-3">
              {/* Search */}
              <div className="relative">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nombre, frecuencia, ubicación o dueño..."
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all"
                />
              </div>

              {/* Filter pills */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Plan filter */}
                <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide whitespace-nowrap">Plan:</span>
                <div className="flex items-center gap-1 bg-background-100 rounded-xl p-1">
                  {allPlans.map((p) => (
                    <button
                      key={p}
                      onClick={() => setFilterPlan(p)}
                      className={`px-3 py-1.5 rounded-lg text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap ${
                        filterPlan === p
                          ? 'bg-background-50 text-foreground-900 shadow-sm'
                          : 'text-foreground-400 hover:text-foreground-600'
                      }`}
                    >
                      {p === 'todos' ? 'Todos' : planLabels[p]} ({planCounts[p] || 0})
                    </button>
                  ))}
                </div>

                <span className="w-px h-5 bg-background-300 hidden sm:block"></span>

                {/* Status filter */}
                <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide whitespace-nowrap">Estado:</span>
                <div className="flex items-center gap-1 bg-background-100 rounded-xl p-1">
                  {allStatuses.map((s) => (
                    <button
                      key={s}
                      onClick={() => setFilterStatus(s)}
                      className={`px-3 py-1.5 rounded-lg text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap ${
                        filterStatus === s
                          ? 'bg-background-50 text-foreground-900 shadow-sm'
                          : 'text-foreground-400 hover:text-foreground-600'
                      }`}
                    >
                      {s === 'todos' ? 'Todos' : statusLabels[s]} ({statusCounts[s] || 0})
                    </button>
                  ))}
                </div>

                <span className="w-px h-5 bg-background-300 hidden sm:block"></span>

                {/* Category filter - more compact */}
                <span className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide whitespace-nowrap">Categoría:</span>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value as RadioCategory | 'todos')}
                  className="px-3 py-2 rounded-xl border border-background-200 bg-background-50 text-[11px] font-semibold text-foreground-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all"
                >
                  {allCategories.map((c) => (
                    <option key={c} value={c}>
                      {c === 'todos' ? 'Todas las categorías' : categoryLabels[c as RadioCategory]}
                    </option>
                  ))}
                </select>

                {/* Clear filters */}
                {hasFilters && (
                  <button
                    onClick={() => {
                      setFilterPlan('todos');
                      setFilterStatus('todos');
                      setFilterCategory('todos');
                      setSearch('');
                    }}
                    className="ml-auto text-[10px] font-semibold text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
            </div>

            {/* Results count */}
            <div className="flex items-center justify-between">
              <p className="text-xs text-foreground-400">
                Mostrando {filteredRadios.length} de {radios.length} radios
              </p>
            </div>

            {/* LOADING STATE */}
            {loading && (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="hidden lg:block bg-background-50 rounded-2xl border border-background-200 p-4 animate-pulse">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-12 rounded-lg bg-background-200 shrink-0"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-36 rounded-md bg-background-200"></div>
                        <div className="h-3 w-24 rounded-md bg-background-200"></div>
                      </div>
                      <div className="h-6 w-16 rounded-lg bg-background-200"></div>
                      <div className="h-6 w-20 rounded-lg bg-background-200"></div>
                      <div className="h-6 w-16 rounded-lg bg-background-200"></div>
                      <div className="w-8 h-8 rounded-lg bg-background-200"></div>
                    </div>
                  </div>
                ))}
                <div className="lg:hidden space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-background-50 rounded-2xl border border-background-200 p-4 animate-pulse">
                      <div className="flex gap-3">
                        <div className="w-16 h-12 rounded-lg bg-background-200 shrink-0"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 w-28 rounded-md bg-background-200"></div>
                          <div className="h-3 w-20 rounded-md bg-background-200"></div>
                          <div className="h-3 w-full rounded-md bg-background-200"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ERROR STATE */}
            {!loading && error && (
              <div className="text-center py-16 bg-background-50 rounded-2xl border border-background-200">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
                  <i className="ri-error-warning-line text-red-500 text-xl"></i>
                </div>
                <h3 className="text-foreground-800 font-bold text-sm mb-1">Error al cargar las radios</h3>
                <p className="text-foreground-400 text-xs max-w-xs mx-auto leading-relaxed">{error}</p>
                <button
                  onClick={loadRadios}
                  className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-accent-500 text-background-50 text-xs font-bold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line text-sm"></i>
                  Reintentar
                </button>
              </div>
            )}

            {/* Content */}
            {!loading && !error && (
              <>
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto bg-background-50 rounded-2xl border border-background-200">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-background-200">
                        <th className="text-left px-5 py-3 text-[10px] font-bold text-foreground-400 uppercase tracking-wider">
                          <button onClick={() => handleToggleSort('name')} className="flex items-center gap-1 hover:text-foreground-700 cursor-pointer whitespace-nowrap">
                            Radio
                            {sortBy === 'name' && (sortDir === 'desc' ? <i className="ri-arrow-down-s-line text-[9px]"></i> : <i className="ri-arrow-up-s-line text-[9px]"></i>)}
                          </button>
                        </th>
                        <th className="text-left px-4 py-3 text-[10px] font-bold text-foreground-400 uppercase tracking-wider">Plan</th>
                        <th className="text-left px-4 py-3 text-[10px] font-bold text-foreground-400 uppercase tracking-wider">Categoría</th>
                        <th className="text-left px-4 py-3 text-[10px] font-bold text-foreground-400 uppercase tracking-wider">Ubicación</th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-foreground-400 uppercase tracking-wider">
                          <button onClick={() => handleToggleSort('listeners')} className="flex items-center gap-1 hover:text-foreground-700 cursor-pointer ml-auto whitespace-nowrap">
                            Oyentes
                            {sortBy === 'listeners' && (sortDir === 'desc' ? <i className="ri-arrow-down-s-line text-[9px]"></i> : <i className="ri-arrow-up-s-line text-[9px]"></i>)}
                          </button>
                        </th>
                        <th className="text-right px-4 py-3 text-[10px] font-bold text-foreground-400 uppercase tracking-wider">
                          <button onClick={() => handleToggleSort('rating')} className="flex items-center gap-1 hover:text-foreground-700 cursor-pointer ml-auto whitespace-nowrap">
                            Rating
                            {sortBy === 'rating' && (sortDir === 'desc' ? <i className="ri-arrow-down-s-line text-[9px]"></i> : <i className="ri-arrow-up-s-line text-[9px]"></i>)}
                          </button>
                        </th>
                        <th className="text-center px-4 py-3 text-[10px] font-bold text-foreground-400 uppercase tracking-wider">Estado</th>
                        <th className="text-right px-5 py-3 text-[10px] font-bold text-foreground-400 uppercase tracking-wider">Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRadios.map((radio) => (
                        <tr
                          key={radio.id}
                          className="border-b border-background-100 last:border-0 hover:bg-background-50/60 transition-colors cursor-pointer"
                          onClick={() => setExpandedRadio(expandedRadio === radio.id ? null : radio.id)}
                        >
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-3">
                              <div className="w-14 h-10 rounded-lg overflow-hidden shrink-0 bg-background-200">
                                <img
                                  src={radio.imageUrl}
                                  alt={radio.name}
                                  className="w-full h-full object-cover object-top"
                                  loading="lazy"
                                />
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm font-bold text-foreground-900 truncate">{radio.name}</p>
                                <p className="text-[11px] text-foreground-400">{radio.frequency} &bull; {radio.owner}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className={`inline-block px-2.5 py-0.5 rounded-md text-[10px] font-bold ${planColors[radio.plan]}`}>
                              {planLabels[radio.plan]}
                            </span>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className="text-xs text-foreground-600 whitespace-nowrap">{categoryLabels[radio.category]}</span>
                          </td>
                          <td className="px-4 py-3.5">
                            <span className="text-xs text-foreground-600 whitespace-nowrap">{radio.location}, {radio.country}</span>
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            <span className="text-sm font-bold text-foreground-900">{formatNumber(radio.listeners)}</span>
                            {radio.listeners > 0 && (
                              <p className="text-[10px] text-foreground-400">pico {formatNumber(radio.peakListeners)}</p>
                            )}
                          </td>
                          <td className="px-4 py-3.5 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <i className="ri-star-fill text-amber-400 text-xs"></i>
                              <span className="text-sm font-bold text-foreground-900">{radio.rating.toFixed(1)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3.5 text-center">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[10px] font-bold ${statusColors[radio.status]}`}>
                              {radio.status === 'activa' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>}
                              {statusLabels[radio.status]}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewRadio(radio);
                              }}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-background-100 text-foreground-600 text-[11px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-external-link-line text-xs"></i>
                              Ver
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Expanded details */}
                  {expandedRadio && (() => {
                    const radio = filteredRadios.find((r) => r.id === expandedRadio);
                    if (!radio) return null;
                    return (
                      <div className="border-t border-background-200 px-5 py-4 bg-background-50/50">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                          <div>
                            <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide mb-1">Slogan</p>
                            <p className="text-xs text-foreground-700">{radio.slogan}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide mb-1">Contenido</p>
                            <div className="flex items-center gap-3 text-xs text-foreground-600">
                              <span><i className="ri-calendar-line text-foreground-400 mr-0.5"></i> {radio.totalPrograms} prog.</span>
                              <span><i className="ri-live-line text-foreground-400 mr-0.5"></i> {radio.totalLives} lives</span>
                              <span><i className="ri-article-line text-foreground-400 mr-0.5"></i> {radio.totalPosts} posts</span>
                            </div>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide mb-1">Actividad</p>
                            <p className="text-[11px] text-foreground-600">Creada: {new Date(radio.createdAt).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                            <p className="text-[11px] text-foreground-400">Última: {new Date(radio.lastActive).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                          </div>
                          <div>
                            <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide mb-1">Métricas</p>
                            <div className="space-y-0.5 text-[11px] text-foreground-600">
                              <div className="flex items-center justify-between">
                                <span>Oyentes pico</span>
                                <span className="font-bold text-foreground-900">{formatNumber(radio.peakListeners)}</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span>Rating</span>
                                <span className="font-bold text-foreground-900 flex items-center gap-0.5">
                                  <i className="ri-star-fill text-amber-400 text-[10px]"></i>
                                  {radio.rating.toFixed(1)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {filteredRadios.length === 0 && (
                    <div className="text-center py-12">
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-3">
                        <i className="ri-search-line text-foreground-400 text-xl"></i>
                      </div>
                      <p className="text-sm font-semibold text-foreground-600">Sin resultados</p>
                      <p className="text-xs text-foreground-400 mt-1">No se encontraron radios con los filtros actuales.</p>
                    </div>
                  )}
                </div>

                {/* Mobile Cards */}
                <div className="lg:hidden space-y-3">
                  {filteredRadios.map((radio) => (
                    <div
                      key={radio.id}
                      className="bg-background-50 rounded-2xl border border-background-200 p-4 cursor-pointer hover:border-background-300 transition-colors"
                      onClick={() => setExpandedRadio(expandedRadio === radio.id ? null : radio.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-16 h-12 rounded-lg overflow-hidden shrink-0 bg-background-200">
                          <img
                            src={radio.imageUrl}
                            alt={radio.name}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <h4 className="text-sm font-bold text-foreground-900 truncate">{radio.name}</h4>
                            <span className={`shrink-0 inline-block px-2 py-0.5 rounded-md text-[9px] font-bold ${planColors[radio.plan]}`}>
                              {planLabels[radio.plan]}
                            </span>
                          </div>
                          <p className="text-[11px] text-foreground-400">{radio.frequency} &bull; {radio.location}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-bold ${statusColors[radio.status]}`}>
                              {radio.status === 'activa' && <span className="w-1 h-1 rounded-full bg-emerald-500"></span>}
                              {statusLabels[radio.status]}
                            </span>
                            <span className="text-[11px] text-foreground-600">
                              <i className="ri-user-voice-line text-foreground-400 mr-0.5"></i>
                              {formatNumber(radio.listeners)}
                            </span>
                            <span className="text-[11px] text-foreground-600">
                              <i className="ri-star-fill text-amber-400 mr-0.5"></i>
                              {radio.rating.toFixed(1)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Expanded mobile details */}
                      {expandedRadio === radio.id && (
                        <div className="mt-3 pt-3 border-t border-background-100 space-y-2">
                          <p className="text-[11px] text-foreground-500 italic">"{radio.slogan}"</p>
                          <div className="flex items-center gap-3 text-[10px] text-foreground-500">
                            <span>{radio.totalPrograms} programas</span>
                            <span>{radio.totalLives} lives</span>
                            <span>{radio.totalPosts} posts</span>
                          </div>
                          <p className="text-[10px] text-foreground-400">
                            Creada: {new Date(radio.createdAt).toLocaleDateString('es-ES')} &bull; Dueño: {radio.owner}
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewRadio(radio);
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-500 text-background-50 text-[11px] font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-external-link-line text-xs"></i>
                            Visitar radio
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {filteredRadios.length === 0 && (
                    <div className="text-center py-12">
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-3">
                        <i className="ri-search-line text-foreground-400 text-xl"></i>
                      </div>
                      <p className="text-sm font-semibold text-foreground-600">Sin resultados</p>
                      <p className="text-xs text-foreground-400 mt-1">No se encontraron radios con los filtros actuales.</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>


    </div>
  );
}